/**
 * @fileoverview Componente responsável por listar tickets na visão customizada.
 */

import React, { useState, useEffect, useLayoutEffect, useReducer, useContext, useRef, useMemo, useCallback } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { VariableSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

import { makeStyles } from "@material-ui/core/styles";

import TicketListItem from "../TicketListItemCustom";
import { NO_QUEUE_SENTINEL } from "../QueuesFilter";
import TicketsListSkeleton from "../TicketsListSkeleton";
import TicketsEmptyState from "../TicketsEmptyState";

import useTickets from "../../hooks/useTickets";
import useTicketCloseButton from "../../hooks/useTicketCloseButton";
import { useConversationQueues } from "../../hooks/useTicketVisibility";
import usePermission from "../../hooks/usePermission";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

// Altura base (ticket sem tags/busca) — calibrada pra bater com a altura real
// renderizada. Valores abaixo foram medidos empiricamente; se errados, a
// VariableSizeList reserva mais/menos que o necessário e cria gap ou overlap
// até o ResizeObserver corrigir. O useLayoutEffect do MeasuredRow ajusta
// antes do paint, mas quanto mais próxima a estimativa, menos jank.
const DEFAULT_ROW_HEIGHT = 62;
const TAGS_ROW_HEIGHT = 24;
const MATCHED_ROW_HEIGHT = 19;
const OVERSCAN_COUNT = 6;
const LOAD_MORE_THRESHOLD = 6;

const estimateRowHeight = (ticket) => {
  if (!ticket) return DEFAULT_ROW_HEIGHT;
  let h = DEFAULT_ROW_HEIGHT;
  if (ticket.tags?.length > 0) h += TAGS_ROW_HEIGHT;
  if (ticket.matchedMessage) h += MATCHED_ROW_HEIGHT;
  return h;
};

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    width: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    background: 'transparent',
  },

  virtualListContainer: {
    flex: 1,
    minHeight: "200px",
    background: 'transparent',
    position: 'relative',
    '& li, & li.MuiListItem-container': {
      listStyle: 'none',
    },
    '& > div > div': {
      ...theme.scrollbarStyles,
    },
  },

  initialSkeletonContainer: {
    flex: 1,
    minHeight: "200px",
    overflow: "hidden",
    position: "relative",
  },

  skeletonFadeOverlay: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background: theme.palette.type === "dark"
      ? "linear-gradient(180deg, rgba(22, 22, 24, 0) 0%, rgba(22, 22, 24, 0) 30%, rgba(22, 22, 24, 1) 100%)"
      : "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 30%, rgba(255, 255, 255, 1) 100%)",
  },

  ticketsListHeader: {
    color: theme.mode === 'light' ? "rgb(67, 83, 105)" : "#EAEAEA",
    zIndex: 2,
    backgroundColor: theme.mode === 'light' ? "white" : theme.palette.background.paper,
    borderBottom: `1px solid ${theme.mode === 'light' ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

}));

const MeasuredRow = React.memo(({ index, style, data }) => {
  const { tickets, searchParam, setRowHeight, now, showUnrepliedClockIcon, canResolve } = data;
  const ticket = tickets[index];
  const rowRef = useRef(null);
  const ticketId = ticket?.id;

  // useLayoutEffect roda síncrono entre commit e paint — a correção de altura
  // acontece antes do browser pintar o frame. Evita o flash de "conteúdo
  // clipado por overflow: hidden" que useEffect causaria ao medir pós-paint.
  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el || ticketId === undefined) return;

    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) setRowHeight(ticketId, h);
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    return undefined;
  }, [ticketId, setRowHeight]);

  if (!ticket) {
    return <div style={style} />;
  }

  return (
    <div style={style}>
      <div ref={rowRef}>
        <TicketListItem
          ticket={ticket}
          searchParam={searchParam}
          now={now}
          showUnrepliedClockIcon={showUnrepliedClockIcon}
          canResolve={canResolve}
        />
      </div>
    </div>
  );
});

// Merge ticket do socket com o existente, preservando campos que o payload
// possa ter omitido (`undefined`). Socket de update parcial (ex.: accept,
// transfer, appMessage) nem sempre inclui tags/presence/pin — sem isso o spread
// apaga o estado local.
const mergePreservingUndefined = (existing, incoming, keys) => {
  const merged = { ...existing, ...incoming };
  for (const k of keys) if (incoming[k] === undefined) merged[k] = existing[k];
  return merged;
};

// Helper: valida se ticket passa pelo filtro de busca
const matchesSearchParam = (ticket, searchParam) => {
  if (!searchParam) return true;

  const search = searchParam.toLowerCase();
  return (
    ticket.contact?.name?.toLowerCase().includes(search) ||
    ticket.contact?.number?.includes(searchParam) ||
    ticket.id?.toString().includes(searchParam) ||
    !!ticket.matchedMessage
  );
};

// Timestamp efetivo para ORDENAR a lista: usa o maior entre messages[0].createdAt
// e updatedAt. Por que o max? Socket de appMessage emite o ticket recarregado
// (updatedAt bumpado) mas SEM o array `messages`; no merge do reducer, o
// `existing.messages` (stale) permanece. Se usarmos só messages[0], o sort
// vê o timestamp antigo e o ticket não sobe. Pegar o max garante que qualquer
// bump legítimo em updatedAt (nova mensagem, accept, pin) reordena o ticket.
const ticketTimestamp = (t) => {
  const msgTs = (() => {
    const last = t?.messages?.[0]?.createdAt;
    if (!last) return 0;
    const n = new Date(last).getTime();
    return Number.isNaN(n) ? 0 : n;
  })();
  const updTs = (() => {
    if (!t?.updatedAt) return 0;
    const n = new Date(t.updatedAt).getTime();
    return Number.isNaN(n) ? 0 : n;
  })();
  return Math.max(msgTs, updTs);
};

// Helper: mantém tickets fixados no topo (companyId-wide, máx 3). Entre pins,
// pinnedAt DESC (último fixado primeiro). Não-pinados ordenados pelo horário
// da última mensagem (DESC), mesmo critério do WhatsApp — isso garante que
// um ticket desfixado "caia" pra posição cronológica correta imediatamente,
// sem precisar de F5.
const sortTickets = (list) => {
  const pinned = [];
  const unpinned = [];
  for (const t of list) {
    if (t.isPinned) pinned.push(t);
    else unpinned.push(t);
  }
  pinned.sort((a, b) => {
    const pa = a.pinnedAt ? new Date(a.pinnedAt).getTime() : 0;
    const pb = b.pinnedAt ? new Date(b.pinnedAt).getTime() : 0;
    return pb - pa;
  });
  unpinned.sort((a, b) => ticketTimestamp(b) - ticketTimestamp(a));
  return [...pinned, ...unpinned];
};

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;
    // Quando for uma nova página 1 (filtro mudou ou fetch inicial), substitui
    // o estado. Sem isso, um LOAD_TICKETS com payload menor só *acrescenta*
    // mensagens ao estado anterior — o que faz parecer que o filtro não
    // aplicou, porque os tickets antigos continuam na lista.
    let updatedState = action.reset ? [] : [...state];

    newTickets.forEach((ticket) => {
      // ✅ VALIDAR: Skip tickets que não passam pelo searchParam
      if (!matchesSearchParam(ticket, action.searchParam)) {
        return; // Pula este ticket
      }

      const ticketIndex = updatedState.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        // Ticket já existe — paginação por scroll está re-baixando a lista.
        // Campos governados em tempo real por socket events (unreadMessages,
        // lastMessage, presence, updatedAt) ficam no estado local; sobrescrever
        // com o valor do servidor descarta RESET_UNREAD/SET_UNREAD aplicados
        // entre o último fetch e este. Campos de identidade/atribuição (status,
        // userId, queueId, tags, isPinned) podem ter sido alterados por outra
        // sessão e são aceitos do servidor.
        const existing = updatedState[ticketIndex];
        const merged = {
          ...ticket,
          unreadMessages: existing.unreadMessages,
          lastMessage: existing.lastMessage ?? ticket.lastMessage,
          presence: ticket.presence ?? existing.presence,
          updatedAt: existing.updatedAt ?? ticket.updatedAt
        };
        updatedState = [
          ...updatedState.slice(0, ticketIndex),
          merged,
          ...updatedState.slice(ticketIndex + 1)
        ];
      } else {
        // Add new ticket immutably
        updatedState = [...updatedState, ticket];
      }
    });

    return sortTickets(updatedState);
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);

    if (ticketIndex !== -1) {
      // Update ticket immutably
      return [
        ...state.slice(0, ticketIndex),
        { ...state[ticketIndex], unreadMessages: 0 },
        ...state.slice(ticketIndex + 1)
      ];
    }
    return state;
  }

  if (action.type === "SET_UNREAD") {
    // Atualiza só `unreadMessages` sem substituir o ticket inteiro, preservando
    // `updatedAt`, `messages`, includes e posição na lista.
    const { ticketId, unreadMessages } = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);

    if (ticketIndex !== -1) {
      return [
        ...state.slice(0, ticketIndex),
        { ...state[ticketIndex], unreadMessages },
        ...state.slice(ticketIndex + 1)
      ];
    }
    return state;
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    // ✅ VALIDAR: Se não passa pelo filtro, REMOVER da lista
    if (!matchesSearchParam(ticket, action.searchParam)) {
      return state.filter((t) => t.id !== ticket.id);
    }

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);

    if (ticketIndex !== -1) {
      const existing = state[ticketIndex];
      // Merge defensivo: o socket de update (ex.: aceitar ticket, transferir)
      // não inclui `messages`, e o timestamp exibido vem de `messages[0].createdAt`
      // quando presente. Preservar campos ausentes evita que `updatedAt` (bumpado
      // no accept) vire o horário exibido. F5 popula messages via ListTickets,
      // mas sockets intermediários não — por isso o merge.
      const merged = mergePreservingUndefined(existing, ticket, ["presence", "tags"]);
      // Se o pin foi alterado, stampa timestamp pra o card animar um flash
      // na nova posição (highlight pulse). O TicketListItemCustom lê esse
      // campo e aplica a classe de animação se for recente (<700ms).
      if (
        ticket.isPinned !== undefined &&
        existing.isPinned !== ticket.isPinned
      ) {
        merged._pinToggledAt = Date.now();
      }
      return sortTickets([
        ...state.slice(0, ticketIndex),
        merged,
        ...state.slice(ticketIndex + 1)
      ]);
    } else {
      // Add new ticket at beginning immutably
      return sortTickets([ticket, ...state]);
    }
  }

  if (action.type === "UPDATE_PRESENCE") {
    const { ticketId, presence } = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);

    if (ticketIndex === -1) return state;
    if (state[ticketIndex].presence === presence) return state;

    return [
      ...state.slice(0, ticketIndex),
      { ...state[ticketIndex], presence },
      ...state.slice(ticketIndex + 1)
    ];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    // ✅ VALIDAR: Se não passa pelo filtro, REMOVER da lista
    if (!matchesSearchParam(ticket, action.searchParam)) {
      return state.filter((t) => t.id !== ticket.id);
    }

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);

    if (ticketIndex !== -1) {
      const existing = state[ticketIndex];
      // Merge defensivo: o payload do socket pode não incluir campos como
      // isPinned/pinnedAt dependendo do emissor. Preservamos campos do
      // existing que o socket não enviou — mesma lógica do UPDATE_TICKET.
      const merged = mergePreservingUndefined(existing, ticket, [
        "presence",
        "isPinned",
        "pinnedAt",
        "tags",
      ]);
      const otherTickets = [
        ...state.slice(0, ticketIndex),
        ...state.slice(ticketIndex + 1)
      ];
      return sortTickets([merged, ...otherTickets]);
    } else {
      // Add new ticket at beginning immutably
      return sortTickets([ticket, ...state]);
    }
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const ticketIndex = state.findIndex((t) => t.contactId === contact.id);

    if (ticketIndex !== -1) {
      const updatedTicket = { ...state[ticketIndex], contact };

      // ✅ VALIDAR: Se não passa mais pelo filtro, REMOVER
      if (!matchesSearchParam(updatedTicket, action.searchParam)) {
        return state.filter((t) => t.id !== updatedTicket.id);
      }

      // Update ticket contact immutably
      return sortTickets([
        ...state.slice(0, ticketIndex),
        updatedTicket,
        ...state.slice(ticketIndex + 1)
      ]);
    }

    return state;
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    // Filter out deleted ticket immutably
    return state.filter((t) => t.id !== ticketId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

// Ticket a que um aviso da lista se refere (para juntar avisos seguidos).
const getActionTicketId = (action) => {
  switch (action.type) {
    case "UPDATE_TICKET_UNREAD_MESSAGES":
    case "UPDATE_TICKET":
      return action.payload?.id;
    case "DELETE_TICKET":
    case "RESET_UNREAD":
      return action.payload;
    case "SET_UNREAD":
      return action.payload?.ticketId;
    default:
      return undefined;
  }
};

const TicketsListCustom = (props) => {
  const {
    status,
    searchParam,
    tags,
    withoutTags = false,
    users,
    selectedWhatsappIds = [],
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    // NOVO: prop com filtro de não lidas
    withUnreadMessages,
    disableItemRendering = false,
    pendingCount = 0,
    pendingHasMore = false,
    onViewPending,
    onAdjustFilters,
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  // Tick global a cada 60s para reavaliar "sem resposta há +10min" no item.
  // Só faz sentido na aba Atendendo — nas outras abas evitamos o re-render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== "open") return undefined;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [status]);
  // Setting "unrepliedClockIcon" — controla o ícone vermelho de sem resposta
  // ao lado do contato. Default: disabled.
  const [showUnrepliedClockIcon, setShowUnrepliedClockIcon] = useState(false);
  const canResolve = useTicketCloseButton();
  const { user } = useContext(AuthContext);
  const { profile } = user;
  // As filas que a lista mostra ao atendente e a seleção padrão seguem quem vê
  // as conversas na empresa (hooks/useTicketVisibility); o servidor confere o mesmo.
  const { visibility, filterQueues, defaultQueues } = useConversationQueues();
  // Filtra pelas filas quem não vê a empresa toda: hoje, o perfil atendente; com
  // a regra nova ligada, quem não tem o nível "company".
  const { enforce } = usePermission();
  const filtersByQueue = enforce ? visibility !== "company" : profile === "user";

  const socketManager = useContext(SocketContext);
  // companyId vem do login e não muda no ciclo de vida do componente —
  // lê uma vez para evitar getItem repetido (cada chamada serializa JSON).
  const companyId = useMemo(() => localStorage.getItem("companyId"), []);

  // ✅ useMemo para estabilizar arrays e evitar re-renders excessivos do useEffect do socket
  const stableTags = useMemo(() => tags, [JSON.stringify(tags)]);
  const stableUsers = useMemo(() => users, [JSON.stringify(users)]);
  const stableSelectedWhatsappIds = useMemo(() => selectedWhatsappIds, [JSON.stringify(selectedWhatsappIds)]);
  const stableSelectedQueueIds = useMemo(() => selectedQueueIds, [JSON.stringify(selectedQueueIds)]);
  const stableQueues = useMemo(() => filterQueues, [JSON.stringify(filterQueues)]);

  // ✅ useRef para filtros - evita closure stale nos handlers do socket
  // Quando filtros mudam, os handlers antigos ainda podem processar eventos
  // Usando ref, eles sempre acessam o valor mais atual
  const tagsRef = useRef(stableTags);
  const usersRef = useRef(stableUsers);
  const selectedWhatsappIdsRef = useRef(stableSelectedWhatsappIds);
  const presenceTimersRef = useRef(new Map());

  const listRef = useRef(null);
  const rowHeightsRef = useRef(new Map());

  // Ref pra acessar o estado atual da lista nos handlers do socket sem causar
  // re-subscribe a cada mudança. Usado pra detectar "ticket já está na lista" —
  // nesse caso, sempre atualizar (bypass de shouldUpdateTicket) porque o LOAD
  // inicial já decidiu que esse ticket pertence à visão do atendente, e seria
  // inconsistente o socket descartar updates dele.
  const ticketsListRef = useRef([]);

  // Avisos do socket que mexem na lista (mensagem nova, atualização, saída,
  // lidas, contato) passam por uma fila só, na ordem em que chegaram: o
  // primeiro entra na hora e os dos 100 ms seguintes entram juntos, numa
  // renderização só. Nenhum se perde (o debounce de antes aplicava só o
  // primeiro e o último da rajada) e nenhum fura a fila (um aviso antigo de
  // mensagem nova aplicado depois da saída do ticket o traria de volta).
  // Avisos de mensagem nova seguidos do mesmo ticket viram o último (o ticket
  // do aviso vem completo): a fila não cresce numa aba escondida.
  const queuedActionsRef = useRef([]);
  const flushTimerRef = useRef(null);
  const dispatchQueued = useCallback((action) => {
    if (flushTimerRef.current) {
      const queue = queuedActionsRef.current;
      const id = getActionTicketId(action);
      if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES" && id != null) {
        for (let i = queue.length - 1; i >= 0; i--) {
          if (getActionTicketId(queue[i]) !== id) continue;
          if (queue[i].type === "UPDATE_TICKET_UNREAD_MESSAGES") queue.splice(i, 1);
          break;
        }
      }
      queue.push(action);
      return;
    }
    dispatch(action);
    const flush = () => {
      const actions = queuedActionsRef.current;
      queuedActionsRef.current = [];
      if (!actions.length) {
        flushTimerRef.current = null;
        return;
      }
      unstable_batchedUpdates(() => actions.forEach((a) => dispatch(a)));
      flushTimerRef.current = setTimeout(flush, 100);
    };
    flushTimerRef.current = setTimeout(flush, 100);
  }, []);
  useEffect(() => () => clearTimeout(flushTimerRef.current), []);

  // ✅ Manter refs atualizados quando filtros mudam
  useEffect(() => {
    tagsRef.current = stableTags;
  }, [stableTags]);

  useEffect(() => {
    usersRef.current = stableUsers;
  }, [stableUsers]);

  useEffect(() => {
    selectedWhatsappIdsRef.current = stableSelectedWhatsappIds;
  }, [stableSelectedWhatsappIds]);

  // Mantém ticketsListRef sincronizado com o estado atual da lista.
  // Os handlers do socket usam essa ref pra decidir se um evento deve ser
  // aplicado mesmo quando shouldUpdateTicket=false (caso o ticket já esteja
  // visível na lista — bug de "badge não atualiza" em tickets sem queue).
  useEffect(() => {
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);

  useEffect(() => {
    // A lista é relida: avisos na fila são da visão anterior.
    queuedActionsRef.current = [];
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, stableTags, stableUsers, stableSelectedWhatsappIds, stableSelectedQueueIds, withUnreadMessages, withoutTags, visibility]);

  // Converte a sentinela "Sem fila" (0) em `null` antes de mandar pro backend.
  // O backend aceita `null` no array de queueIds e gera `queueId IS NULL` via Sequelize.
  const queueIdsForApi = (selectedQueueIds || []).map((id) =>
    id === NO_QUEUE_SENTINEL ? null : id
  );
  const includeNoQueue = (selectedQueueIds || []).indexOf(NO_QUEUE_SENTINEL) > -1;

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    whatsappIds: JSON.stringify(selectedWhatsappIds),
    queueIds: JSON.stringify(queueIdsForApi),
    // NOVO: envia para a API quando ligado
    withUnreadMessages,
    withoutTags: withoutTags ? "true" : undefined,
        // Para aba de grupos, passar isGroup=true, para outras abas não passar nada
        ...(status === "groups" && { isGroup: "true" }),
    // O admin trocou quem vê as conversas: relê com os mesmos filtros.
    reloadKey: visibility,
  });

  // A página lida por ref, não como dependência do efeito abaixo: ao rolar até
  // o fim, a página sobe antes de a próxima chegar, e o efeito reaplicava a
  // resposta velha da página 1, desfazendo o que o socket já tinha atualizado
  // (como o nome do contato) até o próximo aviso daquela conversa.
  const pageNumberRef = useRef(pageNumber);
  useEffect(() => {
    pageNumberRef.current = pageNumber;
  }, [pageNumber]);

  useEffect(() => {
    const userQueueIds = stableQueues.map((q) => q.id);
    const filteredTickets =
      filtersByQueue
        ? tickets.filter((t) => {
            // Tickets sem fila: passam apenas se "Sem fila" estiver marcado.
            if (t.queueId == null) return includeNoQueue;
            return userQueueIds.indexOf(t.queueId) > -1;
          })
        : tickets;

    // Na página 1 (filtro mudou ou mount inicial) substituímos o estado;
    // nas páginas seguintes apenas acumulamos (paginação).
    dispatch({
      type: "LOAD_TICKETS",
      payload: filteredTickets,
      searchParam,
      reset: pageNumberRef.current === 1,
    });
  }, [tickets, stableQueues, filtersByQueue, searchParam, includeNoQueue]);

  // Carrega setting "unrepliedClockIcon" e mantém em sync via socket de settings.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (cancelled) return;
        const s = data.find((x) => x.key === "unrepliedClockIcon");
        setShowUnrepliedClockIcon(s?.value === "enabled");
      } catch (err) {
        // silencioso: default já é false
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const socket = socketManager.getSocket(companyId);
    const handler = (data) => {
      if (data?.action === "update" && data?.setting?.key === "unrepliedClockIcon") {
        setShowUnrepliedClockIcon(data.setting.value === "enabled");
      }
    };
    socket.on(`company-${companyId}-settings`, handler);
    return () => {
      socket.off(`company-${companyId}-settings`, handler);
    };
  }, [socketManager, companyId]);

  useEffect(() => {
    const socket = socketManager.getSocket(companyId);

    const shouldUpdateTicket = (ticket) => {
      // Verificar se ticket pertence ao usuário ou showAll está ativo
      const belongsToUser = !ticket.userId || ticket.userId === user?.id || showAll;

      // Verificar se ticket está na fila selecionada.
      // Tickets sem fila (queueId == null) só passam se "Sem fila" estiver marcado.
      const belongsToQueue =
        ticket.queueId == null
          ? includeNoQueue
          : stableSelectedQueueIds.indexOf(ticket.queueId) > -1;

      // Verificar se ticket passa pelo filtro de busca (se houver)
      const matchesSearch = !searchParam || (
        ticket.contact?.name?.toLowerCase().includes(searchParam.toLowerCase()) ||
        ticket.contact?.number?.includes(searchParam) ||
        ticket.id?.toString().includes(searchParam)
      );

      // Verificar filtro de tags (usando ref para evitar closure stale)
      // Se tagsRef.current não estiver vazio, ticket deve ter pelo menos uma das tags selecionadas
      const currentTags = tagsRef.current;
      const matchesTags = !currentTags || currentTags.length === 0 || (
        ticket.tags && ticket.tags.some(ticketTag =>
          currentTags.includes(ticketTag.id)
        )
      );

      // Verificar filtro de usuários (usando ref para evitar closure stale)
      // Se usersRef.current não estiver vazio, ticket deve pertencer a um dos usuários selecionados
      const currentUsers = usersRef.current;
      const matchesUsers = !currentUsers || currentUsers.length === 0 || (
        ticket.userId && currentUsers.includes(ticket.userId)
      );

      const currentWhatsappIds = selectedWhatsappIdsRef.current;
      const matchesWhatsapp = !currentWhatsappIds || currentWhatsappIds.length === 0 || (
        ticket.whatsappId && currentWhatsappIds.includes(ticket.whatsappId)
      );

      return belongsToUser && belongsToQueue && matchesSearch && matchesTags && matchesUsers && matchesWhatsapp;
    };

    const notBelongsToUserQueues = (ticket) => {
      if (ticket.queueId == null) return !includeNoQueue;
      return stableSelectedQueueIds.indexOf(ticket.queueId) === -1;
    };

    const handleReady = () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
    };

    const handleTicket = (data) => {
      // Mesma lógica do handleAppMessage: se ticket já está visível na lista,
      // bypass de shouldUpdateTicket — caso contrário, updates legítimos
      // (lastMessage, lastClientMessageAt, etc.) deletam o ticket por engano.
      const ticketIdForLookup = data?.ticketId || data?.ticket?.id;
      const ticketAlreadyInList = !!ticketIdForLookup &&
        ticketsListRef.current.some(t => t.id === ticketIdForLookup);

      if (data.action === "updateUnread") {
        dispatchQueued({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      if (data.action === "setUnread") {
        dispatchQueued({
          type: "SET_UNREAD",
          payload: {
            ticketId: data.ticketId,
            unreadMessages: data.unreadMessages,
          },
        });
        return;
      }

      // Para grupos, atualizar na aba "groups" apenas se não estiver resolvido
      // Bypass shouldUpdateTicket quando ticket já está na lista (mesmo motivo do handleAppMessage)
      if (data.action === "update" && (shouldUpdateTicket(data.ticket) || ticketAlreadyInList)) {
        if (status === "groups" && data.ticket.isGroup && data.ticket.status !== "closed") {
          dispatchQueued({
            type: "UPDATE_TICKET",
            payload: data.ticket,
            searchParam: searchParam
          });
        } else if (status === "groups" && data.ticket.isGroup && data.ticket.status === "closed") {
          // Se grupo foi resolvido, remover da aba grupos
          dispatchQueued({ type: "DELETE_TICKET", payload: data.ticket.id });
        } else if (status !== "groups" && data.ticket.status === status && !data.ticket.isGroup) {
          // IMPORTANTE: Para abas não-grupos, NUNCA incluir grupos
          dispatchQueued({
            type: "UPDATE_TICKET",
            payload: data.ticket,
            searchParam: searchParam
          });
        } else if (status !== "groups" && data.ticket.status !== status && !data.ticket.isGroup) {
          // ✅ Se o ticket mudou de status (ex: pending → open), remover da aba atual
          // Status mismatch é mudança REAL — não bypassamos aqui.
          dispatchQueued({ type: "DELETE_TICKET", payload: data.ticket.id });
        }
      }
      // shouldUpdate=false E ticket não está na lista: ignora sem dispatch.
      // Não DELETA: a remoção espúria estava sumindo tickets visíveis
      // que o atendente carregou via allTicket / showAll prévio.

      // notBelongsToUserQueues: só deleta se ticket NÃO está na lista local
      // (evita remoção espúria de tickets carregados via allTicket sem queue).
      if (data.action === "update" && notBelongsToUserQueues(data.ticket) && !ticketAlreadyInList) {
        dispatchQueued({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "delete") {
        dispatchQueued({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    };

    const handleAppMessage = (data) => {
      const queueIds = stableQueues.map((q) => q.id);
      // Verifica se o ticket já está visível na lista local. Se sim, qualquer
      // evento de criação de mensagem deve atualizar (mover pra topo, incrementar
      // badge). O LOAD inicial já decidiu que o ticket pertence à visão; seria
      // inconsistente o socket descartar updates dele só porque shouldUpdate=false
      // (ex.: ticket sem queue visto via allTicket=enabled, transferido pra outro
      // user mas ainda sendo monitorado, etc.)
      const ticketAlreadyInList = !!data?.ticket?.id &&
        ticketsListRef.current.some(t => t.id === data.ticket.id);

      // Aviso sem o ticket completo (sem contato, ex.: o de entrada/saída de
      // participante de grupo) só atualiza ticket que já está na lista: não
      // dá para desenhar uma linha nova com ele.
      if (!ticketAlreadyInList && !data.ticket?.contact) return;

      // Atendente: descarta só ticket de outra fila. O dele (inclusive sem
      // fila, como os da carteira) e o sem fila seguem para o
      // shouldUpdateTicket, a mesma regra do aviso de ticket.
      if (
        filtersByQueue &&
        !ticketAlreadyInList &&
        data.ticket?.userId !== user?.id &&
        data.ticket?.queueId != null &&
        queueIds.indexOf(data.ticket.queueId) === -1
      ) {
        return;
      }

      // Para grupos, atualizar mensagens não lidas na aba "groups" independentemente do status
      // Bypass `shouldUpdateTicket` quando o ticket já está na lista local — bug fix
      // de "badge não atualiza" pra tickets sem queue / atribuídos a outros users.
      if (data.action === "create" && (shouldUpdateTicket(data.ticket) || ticketAlreadyInList)) {
        // ✅ SEMPRE atualizar o ticket na lista (mover para topo + atualizar lastMessage)
        // independente de estar visualizando ou não
        if (status === "groups" && data.ticket.isGroup) {
          dispatchQueued({
            type: "UPDATE_TICKET_UNREAD_MESSAGES",
            payload: data.ticket,
            searchParam: searchParam
          });
        } else if (status !== "groups" && (status === undefined || data.ticket.status === status) && !data.ticket.isGroup) {
          // IMPORTANTE: Para abas não-grupos, NUNCA incluir grupos
          dispatchQueued({
            type: "UPDATE_TICKET_UNREAD_MESSAGES",
            payload: data.ticket,
            searchParam: searchParam
          });
        }
      }
    };

    const handleContact = (data) => {
      if (data.action === "update") {
        dispatchQueued({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
          searchParam: searchParam
        });
      }
    };

    const handlePresence = (data) => {
      if (!data || !data.ticketId) return;
      const p = data.presence;
      const existingTimer = presenceTimersRef.current.get(data.ticketId);
      if (existingTimer) {
        clearTimeout(existingTimer);
        presenceTimersRef.current.delete(data.ticketId);
      }
      if (p === "typing" || p === "recording") {
        dispatch({ type: "UPDATE_PRESENCE", payload: { ticketId: data.ticketId, presence: p } });
        const timer = setTimeout(() => {
          dispatch({ type: "UPDATE_PRESENCE", payload: { ticketId: data.ticketId, presence: null } });
          presenceTimersRef.current.delete(data.ticketId);
        }, 10000);
        presenceTimersRef.current.set(data.ticketId, timer);
      } else {
        dispatch({ type: "UPDATE_PRESENCE", payload: { ticketId: data.ticketId, presence: null } });
      }
    };

    socket.on("ready", handleReady);
    socket.on(`company-${companyId}-ticket`, handleTicket);
    socket.on(`company-${companyId}-appMessage`, handleAppMessage);
    socket.on(`company-${companyId}-contact`, handleContact);
    socket.on(`company-${companyId}-presence`, handlePresence);

    return () => {
      socket.off("ready", handleReady);
      socket.off(`company-${companyId}-ticket`, handleTicket);
      socket.off(`company-${companyId}-appMessage`, handleAppMessage);
      socket.off(`company-${companyId}-contact`, handleContact);
      socket.off(`company-${companyId}-presence`, handlePresence);
      presenceTimersRef.current.forEach((timer) => clearTimeout(timer));
      presenceTimersRef.current.clear();
      socket.disconnect();
    };
  }, [status, showAll, user, stableSelectedQueueIds, stableTags, stableUsers, stableSelectedWhatsappIds, searchParam, filtersByQueue, stableQueues, socketManager, companyId, includeNoQueue]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length, { hasMore });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList, hasMore]);

  const loadMore = useCallback(() => {
    setPageNumber((prevState) => prevState + 1);
  }, []);

  const setRowHeight = useCallback((ticketId, size) => {
    if (ticketId === undefined || !size) return;
    if (rowHeightsRef.current.get(ticketId) === size) return;
    rowHeightsRef.current.set(ticketId, size);
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, []);

  const getRowHeight = useCallback(
    (index) => {
      const ticket = ticketsList[index];
      if (!ticket) return DEFAULT_ROW_HEIGHT;
      return rowHeightsRef.current.get(ticket.id) || estimateRowHeight(ticket);
    },
    [ticketsList]
  );

  const itemKey = useCallback((index, data) => data.tickets[index]?.id ?? index, []);

  const itemData = useMemo(
    () => ({ tickets: ticketsList, searchParam, setRowHeight, now, showUnrepliedClockIcon, canResolve }),
    [ticketsList, searchParam, setRowHeight, now, showUnrepliedClockIcon, canResolve]
  );

  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }) => {
      if (!hasMore || loading) return;
      if (visibleStopIndex >= ticketsList.length - LOAD_MORE_THRESHOLD) {
        loadMore();
      }
    },
    [hasMore, loading, ticketsList.length, loadMore]
  );

  // Invalida o cache de positions do react-window sempre que a lista muda —
  // inclui reorder (ticket sobe pro topo ao receber mensagem), add e remove.
  // Sem isso, o cache de offsets fica stale após reorder e aparece gap/overlap
  // entre items que mantiveram altura mas mudaram de posição.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [ticketsList]);

  const hasItems = ticketsList.length > 0;
  const showInitialSkeleton = !hasItems && loading && !disableItemRendering;
  const showEmptyState = !hasItems && !loading && !disableItemRendering;
  // A seleção padrão inclui as filas permitidas e "Sem fila"; ela não é
  // um filtro adicional. Compara conjuntos, pois a ordem pode mudar no modal.
  const defaultQueueIds = [...defaultQueues.map((queue) => queue.id), NO_QUEUE_SENTINEL];
  const hasActiveFilters = Boolean(
    tags?.length || withoutTags || users?.length || selectedWhatsappIds.length ||
    withUnreadMessages === "true" || withUnreadMessages === true ||
    (selectedQueueIds && (
      selectedQueueIds.length !== defaultQueueIds.length ||
      defaultQueueIds.some((id) => !selectedQueueIds.includes(id))
    ))
  );

  return (
    <div className={classes.ticketsListWrapper} style={style}>
      {showEmptyState && (
        <TicketsEmptyState
          status={status}
          searchParam={searchParam}
          hasActiveFilters={hasActiveFilters}
          showAll={showAll}
          pendingCount={pendingCount}
          pendingHasMore={pendingHasMore}
          onViewPending={onViewPending}
          onAdjustFilters={onAdjustFilters}
        />
      )}
      {showInitialSkeleton && (
        <div className={classes.initialSkeletonContainer}>
          <TicketsListSkeleton />
          <TicketsListSkeleton />
          <TicketsListSkeleton />
          <TicketsListSkeleton />
          <TicketsListSkeleton />
          <div className={classes.skeletonFadeOverlay} />
        </div>
      )}
      {hasItems && !disableItemRendering && (
        <div className={classes.virtualListContainer}>
          {/* A largura em CSS acompanha o divisor no mesmo frame, sem esperar
              a medição assíncrona do AutoSizer nem renderizar a lista a cada px. */}
          <AutoSizer disableWidth>
            {({ height }) => (
              <VariableSizeList
                ref={listRef}
                height={height}
                width="100%"
                itemCount={ticketsList.length}
                itemSize={getRowHeight}
                itemData={itemData}
                itemKey={itemKey}
                overscanCount={OVERSCAN_COUNT}
                onItemsRendered={handleItemsRendered}
              >
                {MeasuredRow}
              </VariableSizeList>
            )}
          </AutoSizer>
        </div>
      )}
    </div>
  );
};

export default TicketsListCustom;
